import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg: '#0F1117', surface: '#1A1D27', surface2: '#22263A',
 border: 'rgba(255,255,255,.08)', border2: 'rgba(255,255,255,.14)',
 text: '#F0F2FF', text2: '#A0A8C0', text3: '#606880',
 accent: '#3AAF58', blue: '#2563A8', blueLight: '#3B82F6',
 red: '#E05050', redBg: 'rgba(224,80,80,.10)', redBorder: 'rgba(224,80,80,.25)',
 amber: '#D4840F', amberBg: 'rgba(212,132,15,.10)', amberBorder: 'rgba(212,132,15,.25)',
 green: '#3AAF58',
};

const inr = n => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_COLORS = {
 'Paid': '#3AAF58', 'Partial': '#D4840F', 'Unpaid': '#E05050', 'Cancelled': '#606880',
};

const PAYMENT_MODES = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Card', 'NEFT', 'RTGS'];
const INVOICE_TYPES = ['Room', 'Event', 'Venue', 'Day Picnic', 'Corporate', 'Food & Beverage', 'Miscellaneous'];
const GST_CATEGORIES = [
 { label: 'Room Stay (₹2,500–₹7,499) — 12%', cgst: 6, sgst: 6 },
 { label: 'Room Stay (₹7,500+) — 18%', cgst: 9, sgst: 9 },
 { label: 'Day Picnic / Package — 18%', cgst: 9, sgst: 9 },
 { label: 'Wedding / Event — 18%', cgst: 9, sgst: 9 },
 { label: 'Conference / MICE — 18%', cgst: 9, sgst: 9 },
 { label: 'Food & Beverage — 5%', cgst: 2.5, sgst: 2.5 },
 { label: 'No GST — 0%', cgst: 0, sgst: 0 },
];

const inputStyle = {
 width: '100%', padding: '10px 12px', background: '#22263A',
 border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: '#F0F2FF',
 fontSize: 14, boxSizing: 'border-box', outline: 'none',
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };

// ── PRINT INVOICE ─────────────────────────────────────────────────────────────
function printInvoice(inv) {
 const paid = parseFloat(inv.total_amount) - parseFloat(inv.balance_due || 0);
 const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Invoice ${inv.invoice_number}</title>
<style>
 body { font-family: Arial, sans-serif; margin: 0; padding: 40px; color: #222; }
 .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 3px solid #2A7A42; padding-bottom: 20px; }
 .company { }
 .logo { font-size: 36px; font-weight: 900; color: #2A7A42; letter-spacing: -2px; font-family: Georgia, serif; }
 .company h1 { margin: 4px 0 0; font-size: 18px; color: #222; font-weight: 700; }
 .company p { margin: 2px 0; font-size: 11px; color: #555; }
 .invoice-info { text-align: right; }
 .invoice-info h2 { margin: 0; font-size: 22px; color: #2A7A42; }
 .invoice-info p { margin: 2px 0; font-size: 12px; }
 .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; margin-top: 6px;
 background: ${inv.payment_status === 'Paid' ? '#d4edda' : inv.payment_status === 'Partial' ? '#fff3cd' : '#f8d7da'};
 color: ${inv.payment_status === 'Paid' ? '#155724' : inv.payment_status === 'Partial' ? '#856404' : '#721c24'}; }
 .section { margin-bottom: 24px; }
 .section h3 { font-size: 13px; text-transform: uppercase; letter-spacing: .05em; color: #666; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px; }
 table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
 th { background: #f0f7f2; padding: 10px 12px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #444; }
 td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
 .total-row td { font-weight: bold; font-size: 15px; border-top: 2px solid #2A7A42; color: #2A7A42; }
 .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 16px; }
 @media print { body { padding: 20px; } }
</style>
</head>
<body>
<div class="header">
 <div class="company">
 <div class="logo">Botanix</div><h1>Botanix Resort Pvt. Ltd.</h1>
 <p>Village Tikri, Asaothi, Faridabad, Haryana — 121004</p>
 <p>GSTIN: 06AAGCB9754R1ZT | Ph: +91-98100-00000</p>
 <p>Email: info@botanix.in | Web: www.botanix.in</p>
 </div>
 <div class="invoice-info">
 <h2>TAX INVOICE</h2>
 <p><strong>${inv.invoice_number}</strong></p>
 <p>Date: ${fmtDate(inv.invoice_date)}</p>
 <p>Type: ${inv.invoice_type}</p>
 <span class="status-badge">${inv.payment_status}</span>
 </div>
</div>

${inv.gst_number ? `<div class="section"><h3>Bill To</h3><p><strong>GST No:</strong> ${inv.gst_number}</p></div>` : ''}

<div class="section">
 <h3>Invoice Details</h3>
 <table>
 <thead><tr><th>Description</th><th>HSN</th><th>Taxable Amount</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Total</th></tr></thead>
 <tbody>
 <tr>
 <td>${inv.invoice_type} Services</td>
 <td>9963</td>
 <td>${inr(inv.taxable_amount)}</td>
 <td>${inr(inv.cgst)}</td>
 <td>${inr(inv.sgst)}</td>
 <td>${inr(inv.igst || 0)}</td>
 <td>${inr(inv.total_amount)}</td>
 </tr>
 <tr class="total-row">
 <td colspan="6">Total Invoice Amount</td>
 <td>${inr(inv.total_amount)}</td>
 </tr>
 </tbody>
 </table>
</div>

<div class="section">
 <h3>Payment Summary</h3>
 <table>
 <tbody>
 <tr><td>Total Amount</td><td><strong>${inr(inv.total_amount)}</strong></td></tr>
 <tr><td>Amount Paid</td><td style="color:#155724"><strong>${inr(paid)}</strong></td></tr>
 <tr><td>Balance Due</td><td style="color:${parseFloat(inv.balance_due) > 0 ? '#721c24' : '#155724'}"><strong>${inr(inv.balance_due)}</strong></td></tr>
 </tbody>
 </table>
</div>

<div class="footer">
 <p>This is a computer generated invoice. Thank you for choosing Botanix Resort Pvt. Ltd.!</p>
 <p>For queries: accounts@botanix.in | +91-98100-00000</p>
</div>
</body>
</html>`;

 const w = window.open('', '_blank', 'width=800,height=900');
 w.document.write(html);
 w.document.close();
 setTimeout(() => w.print(), 500);
}

function Modal({ title, onClose, children, maxWidth = 640 }) {
 return (
 <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
 <div style={{ background: '#1A1D27', border: '1px solid rgba(255,255,255,.14)', borderRadius: 16, width: '100%', maxWidth, maxHeight: '90vh', overflow: 'auto' }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
 <div style={{ fontWeight: 700, fontSize: 16, color: '#F0F2FF' }}>{title}</div>
 <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#606880', fontSize: 20, cursor: 'pointer' }}></button>
 </div>
 <div style={{ padding: 24 }}>{children}</div>
 </div>
 </div>
 );
}

function Field({ label, children }) {
 return (
 <div style={{ marginBottom: 16 }}>
 <label style={{ display: 'block', fontSize: 12, color: '#A0A8C0', marginBottom: 6, fontWeight: 600 }}>{label}</label>
 {children}
 </div>
 );
}

function NewInvoiceModal({ onClose, onSave }) {
 const [form, setForm] = useState({ invoice_type: 'Room', guest_name: '', gst_number: '', taxable_amount: 0, gst_category: 0, payment_status: 'Unpaid' });
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');
 const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

 const gstCat = GST_CATEGORIES[parseInt(form.gst_category)] || GST_CATEGORIES[0];
 const taxable = parseFloat(form.taxable_amount) || 0;
 const cgst = Math.round(taxable * gstCat.cgst / 100);
 const sgst = Math.round(taxable * gstCat.sgst / 100);
 const total = taxable + cgst + sgst;

 const handleSave = async () => {
 if (!form.guest_name) { setError('Guest name is required'); return; }
 setSaving(true); setError('');
 try {
 const invNum = 'INV-' + Date.now().toString().slice(-6);
 const res = await fetch(`${API}/accounts/invoices`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ invoice_number: invNum, invoice_type: form.invoice_type, guest_name: form.guest_name, gst_number: form.gst_number, taxable_amount: taxable, cgst, sgst, igst: 0, total_amount: total, payment_status: form.payment_status, invoice_date: new Date().toISOString().slice(0, 10) }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error || 'Failed to create invoice');
 onSave(); onClose();
 } catch (err) { setError(err.message); }
 finally { setSaving(false); }
 };

 return (
 <Modal title="New Invoice" onClose={onClose}>
 {error && <div style={{ padding: '10px 14px', background: 'rgba(224,80,80,.10)', border: '1px solid rgba(224,80,80,.25)', borderRadius: 8, color: '#E05050', fontSize: 13, marginBottom: 16 }}>{error}</div>}
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
 <Field label="Guest / Company Name *"><input type="text" value={form.guest_name} onChange={e => set('guest_name', e.target.value)} placeholder="Guest or company name" style={inputStyle} /></Field>
 <Field label="GST Number (B2B)"><input type="text" value={form.gst_number} onChange={e => set('gst_number', e.target.value)} placeholder="Leave blank for B2C" style={inputStyle} /></Field>
 <Field label="Invoice Type"><select value={form.invoice_type} onChange={e => set('invoice_type', e.target.value)} style={selectStyle}>{INVOICE_TYPES.map(t => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={t}>{t}</option>)}</select></Field>
 <Field label="Payment Status"><select value={form.payment_status} onChange={e => set('payment_status', e.target.value)} style={selectStyle}>{['Unpaid', 'Partial', 'Paid'].map(s => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}</select></Field>
 </div>
 <Field label="GST Category"><select value={form.gst_category} onChange={e => set('gst_category', e.target.value)} style={selectStyle}>{GST_CATEGORIES.map((g, i) => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={i} value={i}>{g.label}</option>)}</select></Field>
 <Field label="Taxable Amount (₹)"><input type="number" value={form.taxable_amount} onChange={e => set('taxable_amount', e.target.value)} style={inputStyle} /></Field>
 <div style={{ background: '#22263A', borderRadius: 10, padding: 16, marginBottom: 16 }}>
 <div style={{ fontSize: 12, color: '#A0A8C0', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase' }}>GST Breakdown</div>
 {[{ label: 'Taxable Amount', value: inr(taxable) }, { label: `CGST (${gstCat.cgst}%)`, value: inr(cgst) }, { label: `SGST (${gstCat.sgst}%)`, value: inr(sgst) }].map(r => (
 <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, color: '#A0A8C0' }}><span>{r.label}</span><span>{r.value}</span></div>
 ))}
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, color: '#F0F2FF', borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 10, marginTop: 6 }}><span>Total</span><span style={{ color: '#3AAF58' }}>{inr(total)}</span></div>
 </div>
 <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
 <button onClick={onClose} style={{ padding: '10px 20px', background: 'none', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: '#A0A8C0', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
 <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#3AAF58', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? .6 : 1 }}>{saving ? 'Creating…' : 'Create Invoice'}</button>
 </div>
 </Modal>
 );
}

function PaymentModal({ invoice, onClose, onSave }) {
 const [form, setForm] = useState({ amount: invoice.balance_due || 0, payment_mode: 'Cash', transaction_reference: '', payment_date: new Date().toISOString().slice(0, 10) });
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');
 const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

 const handleSave = async () => {
 if (!form.amount || form.amount <= 0) { setError('Enter a valid amount'); return; }
 setSaving(true); setError('');
 try {
 const res = await fetch(`${API}/accounts/payments`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ ...form, invoice_id: invoice.id }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error || 'Failed to record payment');
 onSave(); onClose();
 } catch (err) { setError(err.message); }
 finally { setSaving(false); }
 };

 const paid = parseFloat(invoice.total_amount) - parseFloat(invoice.balance_due || 0);

 return (
 <Modal title={`Record Payment — ${invoice.invoice_number}`} onClose={onClose} maxWidth={480}>
 {error && <div style={{ padding: '10px 14px', background: 'rgba(224,80,80,.10)', border: '1px solid rgba(224,80,80,.25)', borderRadius: 8, color: '#E05050', fontSize: 13, marginBottom: 16 }}>{error}</div>}
 <div style={{ background: '#22263A', borderRadius: 10, padding: 14, marginBottom: 20 }}>
 <div style={{ fontSize: 13, color: '#A0A8C0', marginBottom: 4 }}>Invoice Total: <strong style={{ color: '#F0F2FF' }}>{inr(invoice.total_amount)}</strong></div>
 <div style={{ fontSize: 13, color: '#A0A8C0', marginBottom: 4 }}>Already Paid: <strong style={{ color: '#3AAF58' }}>{inr(paid)}</strong></div>
 <div style={{ fontSize: 13, color: '#A0A8C0' }}>Balance Due: <strong style={{ color: '#D4840F' }}>{inr(invoice.balance_due)}</strong></div>
 </div>
 <Field label="Payment Amount (₹)"><input type="number" value={form.amount} onChange={e => set('amount', e.target.value)} style={inputStyle} /></Field>
 <Field label="Payment Mode"><select value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)} style={selectStyle}>{PAYMENT_MODES.map(m => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={m}>{m}</option>)}</select></Field>
 <Field label="Transaction Reference"><input type="text" value={form.transaction_reference} onChange={e => set('transaction_reference', e.target.value)} placeholder="UPI ID, Cheque no, NEFT ref..." style={inputStyle} /></Field>
 <Field label="Payment Date"><input type="date" value={form.payment_date} onChange={e => set('payment_date', e.target.value)} style={inputStyle} /></Field>
 <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
 <button onClick={onClose} style={{ padding: '10px 20px', background: 'none', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: '#A0A8C0', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
 <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#3AAF58', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? .6 : 1 }}>{saving ? 'Recording…' : 'Record Payment'}</button>
 </div>
 </Modal>
 );
}

export default function Accounts() {
 const [tab, setTab] = useState('invoices');
 const [invoices, setInvoices] = useState([]);
 const [payments, setPayments] = useState([]);
 const [stats, setStats] = useState({});
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [search, setSearch] = useState('');
 const [filterStatus, setFilterStatus] = useState('All');
 const [showNewInvoice, setShowNewInvoice] = useState(false);
 const [refunds, setRefunds] = useState([]);
 const [processingRefund, setProcessingRefund] = useState(null);
 const [paymentModal, setPaymentModal] = useState(null);

 const loadData = useCallback(async () => {
 setLoading(true); setError('');
 try {
 const [invRes, payRes, statRes] = await Promise.all([
 fetch(`${API}/accounts/invoices`),
 fetch(`${API}/accounts/payments`),
 fetch(`${API}/accounts/stats`),
 ]);
 // Load refunds separately
 fetch(`${API}/accounts/refunds`).then(r=>r.json()).then(d=>setRefunds(Array.isArray(d)?d:[])).catch(()=>{});
 const [inv, pay, stat] = await Promise.all([invRes.json(), payRes.json(), statRes.json()]);
 setInvoices(Array.isArray(inv) ? inv : []);
 setPayments(Array.isArray(pay) ? pay : []);
 setStats(stat || {});
 } catch (err) { setError(err.message); }
 finally { setLoading(false); }
 }, []);

 useEffect(() => { loadData(); }, [loadData]);

 let filteredInv = invoices.filter(i => {
 const q = search.toLowerCase();
 return !q || [i.invoice_number, i.invoice_type, i.gst_number || ''].join(' ').toLowerCase().includes(q);
 });
 if (filterStatus !== 'All') filteredInv = filteredInv.filter(i => i.payment_status === filterStatus);

 return (
 <div style={{ padding: 24, background: '#0F1117', minHeight: '100vh', color: '#F0F2FF', fontFamily: 'Inter, sans-serif' }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
 <div>
 <div style={{ fontSize: 22, fontWeight: 800 }}> Accounts & GST</div>
 <div style={{ fontSize: 13, color: '#A0A8C0', marginTop: 2 }}>Invoices, payments and GST reports</div>
 </div>
 <button onClick={() => setShowNewInvoice(true)} style={{ padding: '10px 20px', background: '#3AAF58', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>+ New Invoice</button>
 </div>

 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
 {[
 { label: "Today's Revenue", value: inr(stats.today || 0), color: '#3B82F6' },
 { label: 'This Month', value: inr(stats.this_month || 0), color: '#3AAF58' },
 { label: 'Total Revenue', value: inr(stats.total || 0), icon: '₹', color: '#3AAF58' },
 { label: 'Outstanding', value: inr(stats.outstanding || 0), color: '#D4840F' },
 ].map(s => (
 <div key={s.label} style={{ background: '#1A1D27', border: '1px solid rgba(255,255,255,.08)', borderRadius: 12, padding: '16px 18px' }}>
 <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
 <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
 <div style={{ fontSize: 12, color: '#A0A8C0', marginTop: 2 }}>{s.label}</div>
 </div>
 ))}
 </div>

 <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#1A1D27', borderRadius: 10, padding: 4, width: 'fit-content' }}>
 {[{ id: 'invoices', label: ' Invoices' }, { id: 'payments', label: ' Payments' }, { id: 'refunds', label: ' Refunds' }].map(t => (
 <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: tab === t.id ? '#3AAF58' : 'transparent', color: tab === t.id ? '#fff' : '#A0A8C0' }}>{t.label}</button>
 ))}
 </div>

 <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
 <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoice number, type..." style={{ ...inputStyle, maxWidth: 280, flex: 1 }} />
 {tab === 'invoices' && (
 <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selectStyle, width: 160 }}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="All">All Status</option>
 {['Paid', 'Partial', 'Unpaid'].map(s => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 )}
 <button onClick={loadData} style={{ padding: '10px 16px', background: '#1A1D27', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: '#A0A8C0', cursor: 'pointer', fontSize: 13 }}> Refresh</button>
 </div>

 {error && <div style={{ padding: '12px 16px', background: 'rgba(224,80,80,.10)', border: '1px solid rgba(224,80,80,.25)', borderRadius: 10, color: '#E05050', marginBottom: 16, fontSize: 13 }}> {error}</div>}
 {loading && <div style={{ textAlign: 'center', padding: 60, color: '#A0A8C0' }}>Loading…</div>}

 {!loading && tab === 'invoices' && (
 <div style={{ background: '#1A1D27', border: '1px solid rgba(255,255,255,.08)', borderRadius: 14, overflow: 'hidden' }}>
 {filteredInv.length === 0
 ? <div style={{ textAlign: 'center', padding: 60, color: '#A0A8C0' }}>No invoices found</div>
 : (
 <div style={{ overflowX: 'auto' }}>
 <table style={{ width: '100%', borderCollapse: 'collapse' }}>
 <thead>
 <tr style={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
 {['Invoice #', 'Type', 'Date', 'Taxable', 'CGST', 'SGST', 'Total', 'Balance', 'Status', 'Actions'].map(h => (
 <th key={h} style={{ padding: '12px 14px', fontSize: 11, color: '#A0A8C0', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {filteredInv.map((inv, i) => (
 <tr key={inv.id} style={{ borderBottom: '1px solid rgba(255,255,255,.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
 <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, color: '#3AAF58', whiteSpace: 'nowrap' }}>{inv.invoice_number}</td>
 <td style={{ padding: '12px 14px', fontSize: 12, color: '#A0A8C0' }}>{inv.invoice_type}</td>
 <td style={{ padding: '12px 14px', fontSize: 12, color: '#A0A8C0', whiteSpace: 'nowrap' }}>{fmtDate(inv.invoice_date)}</td>
 <td style={{ padding: '12px 14px', fontSize: 13, color: '#F0F2FF', whiteSpace: 'nowrap' }}>{inr(inv.taxable_amount)}</td>
 <td style={{ padding: '12px 14px', fontSize: 12, color: '#A0A8C0', whiteSpace: 'nowrap' }}>{inr(inv.cgst)}</td>
 <td style={{ padding: '12px 14px', fontSize: 12, color: '#A0A8C0', whiteSpace: 'nowrap' }}>{inr(inv.sgst)}</td>
 <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, color: '#F0F2FF', whiteSpace: 'nowrap' }}>{inr(inv.total_amount)}</td>
 <td style={{ padding: '12px 14px', fontSize: 13, color: parseFloat(inv.balance_due) > 0 ? '#D4840F' : '#606880', whiteSpace: 'nowrap' }}>{inr(inv.balance_due)}</td>
 <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
 <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: `${STATUS_COLORS[inv.payment_status] || '#606880'}22`, color: STATUS_COLORS[inv.payment_status] || '#606880' }}>
 {inv.payment_status}
 </span>
 </td>
 <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
 <div style={{ display: 'flex', gap: 6 }}>
 <button onClick={() => printInvoice(inv)} style={{ padding: '5px 12px', background: 'rgba(37,99,168,.2)', border: '1px solid rgba(37,99,168,.4)', borderRadius: 6, color: '#3B82F6', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
 Print
 </button>
 {inv.payment_status !== 'Paid' && (
 <button onClick={() => setPaymentModal(inv)} style={{ padding: '5px 12px', background: 'rgba(58,175,88,.15)', border: '1px solid rgba(58,175,88,.3)', borderRadius: 6, color: '#3AAF58', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
 Pay
 </button>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}

 {!loading && tab === 'payments' && (
 <div style={{ background: '#1A1D27', border: '1px solid rgba(255,255,255,.08)', borderRadius: 14, overflow: 'hidden' }}>
 {payments.length === 0
 ? <div style={{ textAlign: 'center', padding: 60, color: '#A0A8C0' }}>No payments recorded</div>
 : (
 <div style={{ overflowX: 'auto' }}>
 <table style={{ width: '100%', borderCollapse: 'collapse' }}>
 <thead>
 <tr style={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
 {['Payment Date', 'Invoice #', 'Mode', 'Amount', 'Reference'].map(h => (
 <th key={h} style={{ padding: '12px 14px', fontSize: 11, color: '#A0A8C0', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {payments.map((p, i) => (
 <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
 <td style={{ padding: '12px 14px', fontSize: 12, color: '#A0A8C0', whiteSpace: 'nowrap' }}>{fmtDate(p.payment_date)}</td>
 <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: '#3AAF58' }}>{p.invoice_id}</td>
 <td style={{ padding: '12px 14px', fontSize: 12, color: '#A0A8C0' }}>{p.payment_mode}</td>
 <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 700, color: '#3AAF58', whiteSpace: 'nowrap' }}>{inr(p.amount)}</td>
 <td style={{ padding: '12px 14px', fontSize: 12, color: '#A0A8C0' }}>{p.transaction_reference || '—'}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}
 {tab === 'refunds' && (
   <div>
     <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
       <div style={{color:'#F0F2FF',fontWeight:700,fontSize:15}}>Refund Ledger</div>
       <div style={{fontSize:12,color:'#606880'}}>
         Total Refunded: ₹{(refunds||[]).filter(r=>r.status==='Processed').reduce((s,r)=>s+parseFloat(r.refund_amount||0),0).toLocaleString('en-IN')}
       </div>
     </div>
     {(refunds||[]).length === 0 ? (
       <div style={{textAlign:'center',padding:40,color:'#606880'}}>No refunds yet</div>
     ) : (
       <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
         <thead>
           <tr style={{background:'rgba(255,255,255,.05)'}}>
             {['Refund #','Guest','Booking','Refund Amt','Mode','Status','Action'].map(h=>(
               <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#606880',textTransform:'uppercase'}}>{h}</th>
             ))}
           </tr>
         </thead>
         <tbody>
           {(refunds||[]).map((r,i)=>(
             <tr key={r.id} style={{borderBottom:'1px solid rgba(255,255,255,.06)',background:i%2===0?'transparent':'rgba(255,255,255,.02)'}}>
               <td style={{padding:'10px 14px',color:'#A0A8C0',fontFamily:'monospace',fontSize:11}}>{r.refund_number}</td>
               <td style={{padding:'10px 14px',fontWeight:700,color:'#F0F2FF'}}>{r.guest_name}</td>
               <td style={{padding:'10px 14px',color:'#606880',fontSize:11}}>{r.booking_code}</td>
               <td style={{padding:'10px 14px',fontWeight:800,color:'#EF4444',fontSize:14}}>₹{Math.round(r.refund_amount||0).toLocaleString('en-IN')}</td>
               <td style={{padding:'10px 14px',color:'#A0A8C0',fontSize:12}}>{r.refund_mode}</td>
               <td style={{padding:'10px 14px'}}>
                 <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,
                   background:r.status==='Processed'?'rgba(58,175,88,.2)':r.status==='Approved'?'rgba(37,99,168,.2)':'rgba(212,132,15,.2)',
                   color:r.status==='Processed'?'#3AAF58':r.status==='Approved'?'#2563A8':'#D4840F'}}>
                   {r.status}
                 </span>
               </td>
               <td style={{padding:'10px 14px'}}>
                 {r.status==='Approved' && (
                   <button onClick={async()=>{
                     const ref = window.prompt('Payment reference number (optional):');
                     const res = await fetch('https://api.botanix.in/api/accounts/refunds/'+r.id+'/process',{
                       method:'PUT',headers:{'Content-Type':'application/json'},
                       body:JSON.stringify({processed_by:'Accounts',payment_reference:ref||null,payment_mode:r.refund_mode})
                     });
                     if(res.ok){setRefunds(p=>p.map(x=>x.id===r.id?{...x,status:'Processed'}:x));}
                   }}
                   style={{padding:'4px 10px',background:'rgba(58,175,88,.2)',color:'#3AAF58',border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                     Process & Pay
                   </button>
                 )}
               </td>
             </tr>
           ))}
         </tbody>
       </table>
     )}
   </div>
 )}

 {showNewInvoice && <NewInvoiceModal onClose={() => setShowNewInvoice(false)} onSave={loadData} />}
 {paymentModal && <PaymentModal invoice={paymentModal} onClose={() => setPaymentModal(null)} onSave={loadData} />}
 </div>
 );
}
